import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { authApi } from '../../api/auth.js';
import { ApiError } from '../../api/client.js';
import { Card } from '../ui/Card.jsx';
import { Input } from '../ui/Input.jsx';
import { Button } from '../ui/Button.jsx';
import { applyServerValidation } from '../../lib/forms.js';
import { t } from '../../lib/i18n.js';

export function PasswordForm() {
  const { register, handleSubmit, reset, setError, watch, formState: { errors } } = useForm({
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' }
  });

  const mutation = useMutation({
    mutationFn: (values) => authApi.changePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword
    }),
    onSuccess: () => {
      toast.success('Password aggiornata.');
      reset({ currentPassword: '', newPassword: '', confirmPassword: '' });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'INVALID_CREDENTIALS') {
        setError('currentPassword', { type: 'server', message: 'Password attuale non corretta' });
        return;
      }
      const applied = applyServerValidation(err, setError, ['currentPassword', 'newPassword']);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  const newPassword = watch('newPassword');

  return (
    <Card className="max-w-2xl">
      <header className="mb-4">
        <h2 className="text-base font-semibold text-ink">Password</h2>
        <p className="text-xs text-ink-muted">Minimo 12 caratteri con maiuscole, minuscole e cifre.</p>
      </header>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <Input
          label="Password attuale"
          type="password"
          autoComplete="current-password"
          error={errors.currentPassword?.message}
          {...register('currentPassword', { required: 'Password attuale richiesta' })}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="Nuova password"
            type="password"
            autoComplete="new-password"
            error={errors.newPassword?.message}
            {...register('newPassword', {
              required: 'Nuova password richiesta',
              minLength: { value: 12, message: 'almeno 12 caratteri' },
              validate: (v) => (
                (/[a-z]/.test(v) && /[A-Z]/.test(v) && /\d/.test(v)) ||
                'serve maiuscola, minuscola e una cifra'
              )
            })}
          />
          <Input
            label="Conferma nuova password"
            type="password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            {...register('confirmPassword', {
              required: 'Conferma la password',
              validate: (v) => v === newPassword || 'le password non coincidono'
            })}
          />
        </div>
        <div className="flex justify-end">
          <Button type="submit" loading={mutation.isPending}>Cambia password</Button>
        </div>
      </form>
    </Card>
  );
}
